import { useCallback, useState } from 'react';
import { generateObjectId, getCurrentTime } from '../utils';
import { socket } from '../providers/socket';
import { throttle } from '../utils/throttler';
import {
  AbstractCallMetric,
  CallAcceptedDto,
  CallEndedDto,
  CallRequestDto,
  Chat,
  ChatTypeEnum,
  ClientErrorCreateDto,
  Conference,
  ConferenceTypeEnum,
  EventTypeEnum,
  Packet,
  SendStatusEnum,
  ActivityStatusEnum,
  VisitInitiateDto,
  VisitUserEndDto,
} from '@matap/shared';
import { actionAddNewChatMessage, actionClearCall, actionSetCallConnection, actionUpdateChat, actionShowAlert } from '@/redux';
import { SocketStateEnum } from '@/constants';
import { useUser } from './use.user';
import { useActiveVisit } from './use.active.visit';
import { useAppDispatch, useAppSelector } from './use.redux';
import { getDeviceProfile } from '@/webrtc/utils/utils';
import { P2PRoom } from '@/webrtc/p2p.room';

export const useSocket = () => {
  const dispatch = useAppDispatch();
  const { activeVisitId } = useActiveVisit();
  const socketState = useAppSelector((state) => state.chatReducer.socketState);
  const { authenticated, userId } = useUser();
  const [isPending, setIsPending] = useState(false);

  const emit = useCallback((event: EventTypeEnum, packet: Packet) => {
    return socket.emit(event, packet);
  }, []);

  const emitWithAck = useCallback(
    (event: EventTypeEnum, packet: Packet, callBack?: (success: boolean, response: any) => void) => {
      setIsPending(true);
      socket
        .timeout(10000)
        .emitWithAck(event, packet)
        .then((response: any) => {
          if (response?.status === 'error') {
            callBack?.(false, null);
            response.message && dispatch(actionShowAlert(response.message));
            return;
          }
          callBack?.(true, response);
        })
        .catch((e) => {
          console.warn('error when emitWithAck()', event, e);
          dispatch(actionShowAlert('خطا در انجام درخواست'));
          callBack?.(false, null);
        })
        .finally(() => {
          setIsPending(false);
        });
    },
    [dispatch],
  );

  const sendActivityStatus = useCallback(
    (status: ActivityStatusEnum) => {
      activeVisitId &&
        throttle(EventTypeEnum.ACTIVITY_STATUS, 1000, { policy: 'drop' }, () => emit(EventTypeEnum.ACTIVITY_STATUS, { visitId: activeVisitId, payload: { status } }));
    },
    [activeVisitId, emit],
  );

  const acceptCall = useCallback(
    (conferenceId: string, callBack: (conference: Conference) => void) =>
      emitWithAck(EventTypeEnum.CALL_ACCEPTED, { visitId: activeVisitId, payload: { _id: conferenceId } as CallAcceptedDto }, (success, response) => {
        if (success) {
          callBack(response);
        }
      }),
    [activeVisitId, emitWithAck],
  );

  const hangupCall = useCallback(
    (conferenceId: string, reason?: string) =>
      activeVisitId && emit(EventTypeEnum.CALL_ENDED, { visitId: activeVisitId, payload: { _id: conferenceId, reason } as CallEndedDto }),
    [activeVisitId, emit],
  );

  const initiateVisit = useCallback(
    (payload: VisitInitiateDto) => {
      return emitWithAck(EventTypeEnum.VISIT_INITIATED, { payload } as Packet<VisitInitiateDto>);
    },
    [emitWithAck],
  );

  const acceptVisit = useCallback(
    ({ visitId }: { visitId: string }) => {
      return emitWithAck(EventTypeEnum.VISIT_ACCEPTED, { visitId } as Packet<void>);
    },
    [emitWithAck],
  );

  const endVisit = useCallback(
    ({ visitId, refundMoney }: VisitUserEndDto & { visitId: string }) => {
      return emitWithAck(EventTypeEnum.VISIT_ENDED, { visitId, payload: { refundMoney } } as Packet<VisitUserEndDto>);
    },
    [emitWithAck],
  );

  const sendAnalytic = useCallback(
    (analytic: AbstractCallMetric<any>) => {
      if (!analytic) {
        throw new Error('Analytic can not be null');
      }
      activeVisitId &&
        emit(EventTypeEnum.CALL_ANALYTICS, {
          visitId: activeVisitId,
          payload: analytic,
        } as Packet<AbstractCallMetric<any>>);
    },
    [activeVisitId, emit],
  );

  const createConferenceRoom = useCallback(
    (conference: Conference) => {
      const room = new P2PRoom(conference, {
        userId,
      })
        .on('close', (error) => {
          error && console.error(error);
          dispatch(actionClearCall());
          if (error) {
            hangupCall(room._id);
          }
        })
        .on('analytic', (event) => {
          sendAnalytic(event);
        })
        .on('error', (e) => {
          console.error(e);
        });

      return room;
    },
    [dispatch, hangupCall, sendAnalytic, userId],
  );

  const initiateCall = useCallback(
    async (type: ConferenceTypeEnum) => {
      const deviceProfile = await getDeviceProfile();
      emitWithAck(
        EventTypeEnum.CALL_REQUEST,
        {
          visitId: activeVisitId,
          payload: { type, deviceProfile },
        } as Packet<CallRequestDto>,
        (success, response) => {
          if (!success) {
            return;
          }
          dispatch(actionSetCallConnection(createConferenceRoom(response)));
        },
      );
    },
    [activeVisitId, createConferenceRoom, dispatch, emitWithAck],
  );

  const sendClientError = useCallback(
    (error: ClientErrorCreateDto) => {
      authenticated && emit(EventTypeEnum.CLIENT_ERROR, { payload: error });
    },
    [authenticated, emit],
  );

  const deleteChat = useCallback(
    (chatId: string) => {
      activeVisitId && emit(EventTypeEnum.DELETE_CHAT, { visitId: activeVisitId, payload: { _id: chatId } });
    },
    [activeVisitId, emit],
  );

  const editChat = useCallback(
    (chatId: string, text: string) => {
      activeVisitId && emit(EventTypeEnum.UPDATE_CHAT, { visitId: activeVisitId, payload: { _id: chatId, text } });
    },
    [activeVisitId, emit],
  );

  const resendChat = useCallback(
    (chat: Chat) => {
      if (!activeVisitId) {
        return;
      }

      dispatch(actionUpdateChat({ _id: chat._id, sendStatus: SendStatusEnum.SENDING }));

      emitWithAck(EventTypeEnum.MESSAGE, { visitId: activeVisitId, payload: chat }, (success) => {
        if (success) {
          dispatch(actionUpdateChat({ _id: chat._id, sendStatus: SendStatusEnum.SENT }));
        } else {
          dispatch(actionUpdateChat({ _id: chat._id, sendStatus: SendStatusEnum.FAILED }));
        }
      });
    },
    [activeVisitId, dispatch, emitWithAck],
  );

  return {
    resendChat,
    deleteChat,
    editChat,
    acceptCall,
    sendActivityStatus,
    hangupCall,
    emit,
    sendAnalytic,
    sendClientError,
    socket,
    isConnected: socketState === SocketStateEnum.CONNECTED,
    initiateCall,
    createConferenceRoom,
    initiateVisit,
    acceptVisit,
    endVisit,
    isPending,
  };
};
